import { Flex, IconV2, TextV1 } from '@withjoy/joykit';
import React, { FC, useEffect, useRef, useState } from 'react';
import { ToastContainerProps, ToastProps } from 'react-toast-notifications';
import { getTransitionStates, StyledDefaultToastContainer, StyledToastText, StyledToastWrapper, ToastContent, ToastIcon, ToastWrapper } from './Toast.styles';
import { CheckOutline, Info, Tip, Warning } from '@withjoy/joykit/icons';
import { useIsMounted } from '@shared/utils/hooks/useIsMounted';
import { SharedToastProps, ToastProps as ToastInternalProps } from './Toast.type';
import { animated, AnimatedValue, useSpring, useTransition, UseTransitionProps, UseTransitionResult } from 'react-spring';
import { TIME_TO_DELETE, TIME_TO_LIVE, TRANSITION_PROPS } from './Toast.constants';
import { withWindow } from '@shared/utils/withWindow';
import ReactDOM from 'react-dom';

type PrimaryToastProps = ToastProps & { className?: string };

export enum AUTO_DISMISS_TIMEOUT {
  BASE = 4000
}

function SimpleTransition<T>(props: {
  item: T;
  keys?: (item: T) => string;
  values: UseTransitionProps<T, React.CSSProperties>;
  children: (value: UseTransitionResult<T, React.CSSProperties>) => React.ReactNode;
}) {
  const transitions = useTransition(props.item, props.keys || null, props.values);
  return <>{transitions.map(value => value.item && <React.Fragment key={value.key}>{props.children(value)}</React.Fragment>)}</>;
}

export const RegistryToast: React.FC<ToastProps> = ({ children, appearance }) => (
  <StyledToastWrapper>
    <Flex alignItems="center" columnGap={['1rem', '0.5rem']}>
      <IconV2 size="md" display="flex" color={'white'}>
        {appearance === 'error' ? <Info /> : <CheckOutline />}
      </IconV2>
      <StyledToastText variant="label2">{children}</StyledToastText>
    </Flex>
  </StyledToastWrapper>
);

export const PrimaryToast: React.FC<PrimaryToastProps> = ({ children, appearance, className }) => (
  <StyledToastWrapper className={className}>
    <Flex alignItems="center">
      <StyledToastText variant="label2">{children}</StyledToastText>
    </Flex>
  </StyledToastWrapper>
);

export const ToastContainer: React.FC<ToastContainerProps> = ({ children, ...props }) => {
  return <StyledDefaultToastContainer {...props}>{children}</StyledDefaultToastContainer>;
};

export const AnimatedToastContainer: React.FC<ToastContainerProps> = props => {
  return (
    <SimpleTransition values={TRANSITION_PROPS} item={props.hasToasts}>
      {transitionResult => (
        <animated.div
          key={transitionResult.key}
          style={{ ...transitionResult.props, minHeight: 80, position: 'fixed', bottom: 86, width: '100%', overflow: 'visible', zIndex: 201 }}
        >
          <ToastContainer {...props} />
        </animated.div>
      )}
    </SimpleTransition>
  );
};

export const Toast: FC<ToastInternalProps> = ({ title, description, duration = TIME_TO_LIVE, status = 'error', variant = 'toast' }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isClosing, setIsClosing] = useState(false);

  useIsMounted();

  const appRootRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    withWindow(global => {
      appRootRef.current = global.document.getElementById('root');
    });
  }, []);

  useEffect(() => {
    if (isOpen) {
      const timeout = setTimeout(() => {
        setIsClosing(true);
      }, duration);
      return () => {
        clearTimeout(timeout);
      };
    }
    return undefined;
  }, [isOpen, duration]);

  useEffect(() => {
    if (isOpen && isClosing) {
      const timeout = setTimeout(() => {
        setIsOpen(false);
      }, TIME_TO_DELETE);
      return () => {
        clearTimeout(timeout);
      };
    }
    return undefined;
  }, [isOpen, isClosing]);

  let icon = <Tip />;
  switch (status) {
    case 'error':
    case 'warning':
      icon = <Warning />;
      break;
    default:
      icon = <Tip />;
      break;
  }

  const sharedProps: SharedToastProps = {
    $variant: variant,
    $isOpen: !isClosing
  };

  const springs = useSpring({
    ...getTransitionStates(sharedProps),
    config: {
      clamp: true
    }
  }) as AnimatedValue<{ opacity: number; transform: string }>;

  const toast = (
    <ToastWrapper as={animated.div} status={status} variant={variant} style={springs}>
      <ToastIcon>{icon}</ToastIcon>
      <ToastContent>
        <TextV1 variant="hed1">{title}</TextV1>
        <TextV1 marginTop={5} variant="hed1" component="p" fontWeight={400}>
          {description}
        </TextV1>
      </ToastContent>
    </ToastWrapper>
  );

  if (variant === 'toast') {
    if (!appRootRef.current) {
      return null;
    }
    return isOpen ? ReactDOM.createPortal(toast, appRootRef.current) : null;
  }

  return isOpen ? toast : null;
};
